import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, Mail, Phone, X, Plus } from "lucide-react";
import LeadDialog from "@/components/site/LeadDialog";
import { whatsappUrl, mailtoUrl, CONTACT_EMAIL } from "@/lib/channels";

/**
 * Floating contact widget pinned to the bottom-right of every page.
 * Three channels: WhatsApp (instant), Email (async), Call-back request (lead form).
 *
 * Collapses to a single floating action button to keep the viewport uncluttered.
 */
export default function FloatingContact() {
  const [open, setOpen] = useState(false);

  return (
    <div className="fixed bottom-4 right-4 z-40 flex flex-col items-end gap-3" data-testid="floating-contact">
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.95 }}
            transition={{ duration: 0.18 }}
            className="flex flex-col items-end gap-2.5"
          >
            <FabItem
              href={whatsappUrl()}
              external
              label="WhatsApp"
              hint="Reply within minutes"
              icon={<MessageCircle className="h-5 w-5" />}
              tone="emerald"
              testid="fab-whatsapp"
            />
            <FabItem
              href={mailtoUrl()}
              label="Email"
              hint={CONTACT_EMAIL}
              icon={<Mail className="h-5 w-5" />}
              testid="fab-email"
            />
            <LeadDialog
              source="floating_callback"
              titleVariant="callback"
              trigger={
                <button type="button" className="group" data-testid="fab-callback">
                  <FabContent
                    label="Request Call Back"
                    hint="We'll call you"
                    icon={<Phone className="h-5 w-5" />}
                  />
                </button>
              }
            />
          </motion.div>
        )}
      </AnimatePresence>

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? "Close contact options" : "Open contact options"}
        data-testid="fab-toggle"
        className={
          "h-14 w-14 rounded-full grid place-items-center text-[#071A2D] shadow-[0_18px_60px_rgba(22,213,255,0.45)] transition-transform " +
          (open ? "scale-95" : "scale-100 animate-pulseGlow")
        }
        style={{ background: "linear-gradient(135deg, #16D5FF, #1657C8)" }}
      >
        {open ? <X className="h-6 w-6" /> : <Plus className="h-6 w-6" />}
      </button>
    </div>
  );
}

function FabContent({ label, hint, icon, tone }) {
  const accent = tone === "emerald" ? "from-emerald-400 to-emerald-600" : "from-cyan to-royal";
  return (
    <div className="group flex items-center gap-3 rounded-full pl-4 pr-2 py-2 bg-[#0B2138]/95 backdrop-blur border border-white/[0.08] shadow-card hover:border-cyan/30 transition">
      <div className="text-right leading-tight">
        <div className="text-[13px] font-semibold text-white">{label}</div>
        <div className="text-[11px] text-ink-400">{hint}</div>
      </div>
      <div className={`h-10 w-10 rounded-full grid place-items-center text-[#071A2D] bg-gradient-to-br ${accent}`}>
        {icon}
      </div>
    </div>
  );
}

function FabItem({ href, external, label, hint, icon, tone, testid }) {
  return (
    <a
      href={href}
      target={external ? "_blank" : undefined}
      rel={external ? "noreferrer" : undefined}
      data-testid={testid}
    >
      <FabContent label={label} hint={hint} icon={icon} tone={tone} />
    </a>
  );
}
